import { getBearerToken } from "@risalabs_frontend_org/auth-wrapper";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logError } from "../../utils/customLogger";

/**
 * Request payload for fetching the detailed audit trail of an order.
 */
export interface GetDetailedAuditTrialPayload {
  /** Unique identifier of the order whose audit trail is requested. */
  order_id: string;
  /** Name of the audit event group to expand into detailed entries. */
  name: string;
  /** Event tag/category used to scope the detailed audit trail. */
  tag: string;
}

/**
 * Fetches the detailed audit trail for a single order from the backend.
 *
 * Attaches an in-memory bearer token to authorize the request.
 *
 * @param payload - Order identifier and event filters for the audit trail
 * @returns The parsed detailed audit trail response from the API
 * @throws {Error} When the request fails or returns a non-OK status
 */
export const getDetailedAuditTrial = async (
  payload: GetDetailedAuditTrialPayload,
) => {
  try {
    const token = await getBearerToken();

    if (!token) {
      throw new Error("Bearer token not available");
    }

    const response = await fetch(API_ENDPOINTS.GET_AUDIT_TRIAL_DETAILS, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error("Failed to fetch audit trial overview");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    logError(error as Error, "Error in getAuditTrialOverview");
    throw error;
  }
};
